import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle2, ArrowRight, Phone, Bot, Calendar, Bell, WifiOff, Copy, AlertTriangle, ChevronLeft, PhoneForwarded, PhoneOff, PhoneMissed, Power, Zap, Users, Check, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ConsentInfoSheet } from '../components/ui/ConsentInfoSheet';
import { WifiInfoSheet } from '../components/ui/WifiInfoSheet';
import { getSubscriptionPlan, DEFAULT_SUBSCRIPTION_PLAN_ID } from '../data/subscriptionPlans';
import { useTheme } from '../contexts/ThemeContext';
import { ProCheckoutScreen } from './ProCheckoutScreen';
interface IosActivationFlowProps {
  onComplete: (enabledConditions: Condition[], destination?: 'home' | 'callerExperience') => void;
  onCancel: () => void;
  onBack: () => void;
  /**
   * Opens the shared support screen (same destination as the Account tab's
   * "Contact Support" entry).
   */
  onContactSupport?: () => void;
  /**
   * When set, the flow runs in "add a single rule" mode for an already-active
   * subscriber: it skips the trial/checkout intro and jumps straight to the
   * carrier-code activation for this one condition.
   */
  addRuleCondition?: Condition;
  /** Subscription was completed immediately before this flow. */
  subscriptionActive?: boolean;
  /** Routes an in-flow subscription through the shared forwarding-number choice. */
  onSubscriptionSuccess?: () => void;
}
type Condition = 'busy' | 'unanswered' | 'unreachable' | 'off' | 'all';
const VEXU_NUMBER = '+1 (555) 019-8472';
const CONDITIONS = [{
  id: 'busy',
  title: 'When my phone is busy',
  desc: 'Whether you manually send a caller the busy tone or your phone is already on a call, your AI assistant will answer.',
  icon: PhoneOff,
  code: '*67*+15550198472#'
}, {
  id: 'unanswered',
  title: "When I don't answer",
  desc: 'Your AI answers after your phone rings unanswered.',
  icon: PhoneMissed,
  code: '*61*+15550198472#'
}, {
  id: 'unreachable',
  title: "When my phone isn't reachable",
  desc: 'Your AI answers if your phone has no signal.',
  icon: WifiOff,
  code: '*62*+15550198472#'
}, {
  id: 'off',
  title: 'When my phone is turned off',
  desc: 'Your AI answers when your phone is powered off.',
  icon: Power,
  code: '*62*+15550198472#'
}];
export function IosActivationFlow({
  onComplete,
  onCancel,
  onBack,
  onContactSupport,
  addRuleCondition,
  subscriptionActive = false,
  onSubscriptionSuccess
}: IosActivationFlowProps) {
  const {
    isLight
  } = useTheme();
  const isAddRule = Boolean(addRuleCondition);
  // A freshly completed subscription skips the introduction and checkout, but
  // retains the existing mobile condition-selection and setup journey.
  const [step, setStep] = useState(isAddRule ? 5 : subscriptionActive ? 4 : 1);
  const [selectedConditions, setSelectedConditions] = useState<Condition[]>([addRuleCondition ?? 'busy']);
  const [currentConditionIdx, setCurrentConditionIdx] = useState(0);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showConsentSheet, setShowConsentSheet] = useState(false);
  const [showWifiSheet, setShowWifiSheet] = useState(false);
  const [verificationState, setVerificationState] = useState<'idle' | 'verifying' | 'troubleshoot' | 'cancelConfirm'>('idle');
  const [copied, setCopied] = useState(false);
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const toggleCondition = (id: Condition) => {
    if (id === 'all') {
      setSelectedConditions(['all']);
      return;
    }
    setSelectedConditions(prev => {
      const newSelection = prev.filter(c => c !== 'all');
      if (newSelection.includes(id)) {
        return newSelection.filter(c => c !== id);
      }
      return [...newSelection, id];
    });
  };
  const conditionsToActivate = selectedConditions.includes('all') ? [{
    id: 'all',
    title: 'Answer All Incoming Calls',
    code: '*21*+15550198472#'
  }] : CONDITIONS.filter(c => selectedConditions.includes(c.id as Condition));
  const currentCondition = conditionsToActivate[currentConditionIdx];
  const handleConditionSuccess = () => {
    if (currentConditionIdx < conditionsToActivate.length - 1) {
      setCurrentConditionIdx(p => p + 1);
      setVerificationState('idle');
    } else {
      setStep(7);
    }
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        return <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-2958" data-mp="3matjrpd0qe6">
            <button onClick={onBack} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-2959" data-mp="2bu65sw0q2u2v">
              <ChevronLeft className="w-5 h-5" data-id="element-2960" data-mp="2kv3zolhg5l7q" />
            </button>

            <div className="mt-12 mb-8 text-center" data-id="element-2961" data-mp="2ksriiajijevq">
              <div className={`w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/20'}`} data-id="element-2962" data-mp="1x4i0ljpf7b9v">
                <Bot className={`w-8 h-8 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2963" data-mp="26ut97jk1u8n3" />
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2964" data-mp="223kzemet4l9e">
                Activate Your AI Assistant
              </h1>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2965" data-mp="19exi11x6ig14">
                Start your 3-day free trial and let your AI answer calls, take
                messages, schedule meetings, and notify you instantly.
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-2966" data-mp="3j8jk9i9traqm">
              {[{
              icon: Phone,
              text: 'AI answers missed calls 24/7'
            }, {
              icon: Bell,
              text: 'Smart call summaries'
            }, {
              icon: Calendar,
              text: 'Meeting scheduling'
            }, {
              icon: Zap,
              text: 'Personalized AI responses'
            }, {
              icon: CheckCircle2,
              text: 'Monthly AI minutes included'
            }].map((benefit, i) => <div key={i} className="flex items-center gap-3" data-id="element-2967" data-mp="juplxz28v9e3" data-mpi={i}>
                  <div className={`p-1.5 rounded-full ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/20 text-vexu'}`} data-id="element-2968" data-mp="249dpgqf1jiwm">
                    <benefit.icon className="w-4 h-4" data-id="element-2969" data-mp="1vi3pohdfeqjh" />
                  </div>
                  <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2970" data-mp="1vg0pj4ik7h11">
                    {benefit.text}
                  </span>
                </div>)}
            </div>

            <div className={`p-4 rounded-2xl mb-8 ${isLight ? 'bg-slate-50 border border-slate-200' : 'bg-white/5 border border-white/10'}`} data-id="element-2971" data-mp="1gkbbptnj2a7e">
              <div className="flex items-center gap-2 mb-2" data-id="element-2972" data-mp="17ky1gyrkxhlm">
                <Shield className={`w-4 h-4 ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2973" data-mp="17mr6k9mjntri" />
                <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2974" data-mp="31xw7352c3q2m">
                  Compatibility Guarantee
                </h3>
              </div>
              <p className={`text-xs leading-relaxed mb-2 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2975" data-mp="2fsfzql34fmdq">
                Vexu works with supported mobile carriers that offer call
                forwarding.
              </p>
              <p className={`text-xs leading-relaxed mb-2 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2976" data-mp="2600i5ovqkztd">
                If your carrier or mobile plan isn't compatible, you can cancel
                your subscription during setup before completing activation.
              </p>
              <p className={`text-xs leading-relaxed font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2977" data-mp="1n38y3d8dego9">
                We'll guide you through every step and verify that your
                activation was successful.
              </p>
            </div>

            <div className="mt-auto" data-id="element-2978" data-mp="15twxnpcaze3i">
              <Button fullWidth onClick={() => setStep(2)} data-id="element-2979" data-mp="15uycm4wfwkqi">
                Start 3-Day Free Trial
              </Button>
            </div>
          </motion.div>;
      case 2:
        return <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="absolute inset-0 z-50 bg-white dark:bg-black" data-id="element-2980" data-mp="1fkrluz940nrl">
            <ProCheckoutScreen plan={getSubscriptionPlan(DEFAULT_SUBSCRIPTION_PLAN_ID)} onBack={() => setStep(1)} onSuccess={() => {
            if (onSubscriptionSuccess) {
              onSubscriptionSuccess();
              return;
            }
            setStep(3);
          }} data-id="element-2981" data-mp="1yj3tggkdhpl2" />
          </motion.div>;
      case 3:
        return <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col items-center justify-center p-6 text-center" data-id="element-2982" data-mp="eyg4jnulxz3c">
            <div className={`w-20 h-20 rounded-full mb-6 flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/20'}`} data-id="element-2983" data-mp="xmtksm3h6nct">
              <span className="text-4xl" data-id="element-2984" data-mp="nx1nvl7wli56">🎉</span>
            </div>
            <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2985" data-mp="nv6zty1c59wv">
              Welcome to Vexu Pro
            </h1>
            <p className={`text-sm leading-relaxed mb-8 max-w-[260px] ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2986" data-mp="1gf5j8xr0d4ho">
              Your AI assistant is now ready. Let's connect your phone so it can
              answer your calls whenever you're unavailable.
            </p>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full mb-12 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-2987" data-mp="1nzg4gxocttpi">
              <span className="text-lg" data-id="element-2988" data-mp="1xwukiw6d8gha">⏱</span>
              <span className="text-xs font-medium" data-id="element-2989" data-mp="22uw5jmncqpve">
                Estimated setup time: About 1 minute
              </span>
            </div>
            <Button fullWidth onClick={() => setStep(4)} data-id="element-2990" data-mp="26uogg607x83x">
              Connect My Phone
            </Button>
          </motion.div>;
      case 4:
        return <motion.div key="step4" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-2991" data-mp="2gmapbvp32v3i">
            <div className="mb-8" data-id="element-2992" data-mp="1vxi587bvtu3x">
              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2993" data-mp="1vyb64ro5tpe5">
                When should your AI answer your calls?
              </h1>
              <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2994" data-mp="25tl7reanbure">
                Choose when your AI should answer for you. You can change these
                settings anytime.
              </p>
            </div>

            <div className="space-y-3 mb-8" data-id="element-2995" data-mp="2tl0ctett4q6c">
              <button type="button" onClick={() => toggleCondition('all')} aria-pressed={selectedConditions.includes('all')} className={`w-full rounded-2xl border p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${selectedConditions.includes('all') ? 'border-vexu bg-vexu/10' : isLight ? 'border-vexu/20 bg-vexu/5 hover:bg-vexu/10' : 'border-vexu/30 bg-vexu/10 hover:bg-vexu/15'}`} data-id="element-2996" data-mp="2tinvn3vvijuc">
                <span className="flex items-center gap-3" data-id="element-2997" data-mp="2w56dndn4k0io">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2998" data-mp="2w4n7eu6u70kh">
                    <PhoneForwarded className="h-5 w-5" data-id="element-2999" data-mp="2n36vuj4acseo" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-3000" data-mp="2r6v4l54nlu1i">
                    <span className={`block text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3001" data-mp="2d6kdc0zfmdft">
                      Answer all incoming calls
                    </span>
                    <span className={`mt-0.5 block text-[11px] leading-snug ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3002" data-mp="benzultpetie">
                      Let Vexu answer every call with one forwarding setup.
                    </span>
                  </span>
                  <ArrowRight className={`h-4 w-4 shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-3003" data-mp="27hu7w74y2tro" />
                </span>
              </button>

              <p className={`pt-2 text-[11px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3004" data-mp="1yhxe5r3p74pr">
                Or choose specific situations
              </p>

              {CONDITIONS.map(condition => {
              const Icon = condition.icon;
              const isSelected = selectedConditions.includes(condition.id as Condition);
              return <button key={condition.id} type="button" onClick={() => toggleCondition(condition.id as Condition)} disabled={selectedConditions.includes('all')} aria-pressed={isSelected} className={`w-full text-left p-4 rounded-2xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${selectedConditions.includes('all') ? 'cursor-not-allowed opacity-50' : isSelected ? isLight ? 'border-slate-400 bg-slate-50' : 'border-zinc-500 bg-white/[0.07]' : isLight ? 'border-slate-200 bg-white hover:border-slate-300' : 'border-white/10 bg-white/5 hover:bg-white/[0.07]'}`} data-id="element-3005" data-mp="1artl2do4ynd0">
                    <div className="flex items-start gap-3" data-id="element-3006" data-mp="33p6ys9j4ffz6">
                      <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isSelected ? isLight ? 'bg-slate-900 text-white' : 'bg-white text-black' : isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3007" data-mp="37pqtz7k4w9io">
                        <Icon className="h-4 w-4" data-id="element-3008" data-mp="2jzwiatbjwr00" />
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-3009" data-mp="3nbc3grwy95m5">
                        <span className={`block text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3010" data-mp="3n9r7qyywfgmp">
                          {condition.title}
                        </span>
                        <span className={`mt-1 block text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3011" data-mp="3s2kcvnya4qzb">
                          {condition.desc}
                        </span>
                      </span>
                      {isSelected && <span className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold ${isLight ? 'bg-slate-900 text-white' : 'bg-white text-black'}`} data-id="element-3012" data-mp="3ddo41ooef9xn">
                          <Check className="h-3 w-3" strokeWidth={3} data-id="element-3013" data-mp="sp1i1p4kw0ni" />
                          Selected
                        </span>}
                    </div>
                  </button>;
            })}
            </div>

            {/* Consent block */}
            <div className={`rounded-xl p-4 mb-8 ${isLight ? 'bg-slate-50 border border-slate-200' : 'bg-white/5 border border-white/10'}`} data-id="element-3014" data-mp="1124ju0khs8n6">
              <div className="flex items-start gap-3" data-id="element-3015" data-mp="r6t4bg19exed">
                <button type="button" role="checkbox" aria-checked={consentChecked} aria-label="Consent to AI call handling terms" onClick={() => setConsentChecked(v => !v)} className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${consentChecked ? 'bg-vexu border-2 border-vexu' : isLight ? 'bg-white border-2 border-slate-300' : 'bg-transparent border-2 border-white/30'}`} data-id="element-3016" data-mp="1evvujc9ylykh">
                  {consentChecked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-3017" data-mp="2ixaeb2u660w" />}
                </button>
                <div className="min-w-0" data-id="element-3018" data-mp="3tx32eibjx3gp">
                  <p className="text-[13px] leading-[1.5]" style={{
                  color: '#6B7280'
                }} data-id="element-3019" data-mp="3twbi43nems5q">
                    I understand my AI assistant will identify itself to callers
                    where required, calls may be recorded or transcribed where
                    legally required, and I authorize forwarding eligible calls
                    to Vexu. I can turn this off anytime through the app's
                    forwarding setup.
                  </p>
                  <button type="button" onClick={() => setShowConsentSheet(true)} className={`inline-flex items-center gap-0.5 mt-2 text-[13px] font-medium underline ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-3020" data-mp="7itjgoqe4nnb">
                    Learn more
                    <ChevronRight className="w-3.5 h-3.5" data-id="element-3021" data-mp="2i5vn5i4o32hk" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-auto" data-id="element-3022" data-mp="2s2i8s94thxkn">
              <Button fullWidth onClick={() => setStep(5)} disabled={selectedConditions.length === 0 || !consentChecked} data-id="element-3023" data-mp="2s1gtttkokqxn">
                Continue
              </Button>
            </div>
          </motion.div>;
      case 5:
        return <motion.div key="step5" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6" data-id="element-3024" data-mp="35sbvolwri7rq">
            {isAddRule && <button onClick={onBack} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-3025" data-mp="1leurgku87r0w">
                <ChevronLeft className="w-5 h-5" data-id="element-3026" data-mp="3tjyk50aokzhe" />
              </button>}

            <div className="mt-12 mb-8 text-center" data-id="element-3027" data-mp="2bugbzy6hync">
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3028" data-mp="73ucdhb4o7en">
                {isAddRule ? 'Add an Answering Rule' : 'Almost Ready'}
              </h1>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3029" data-mp="h1ifovmsun8f">
                {isAddRule ? `We'll set up call forwarding for "${conditionsToActivate[0]?.title}" so your AI can answer in this situation too.` : "Before activating your AI, we need to update your mobile carrier's call forwarding settings."}
              </p>
            </div>

            <div className={`p-5 rounded-3xl mb-8 ${isLight ? 'bg-amber-50 border border-amber-200' : 'bg-amber-500/10 border border-amber-500/20'}`} data-id="element-3030" data-mp="2rw67cicsfh16">
              <div className="flex items-center gap-3 mb-3" data-id="element-3031" data-mp="2fispg1fdb28x">
                <div className="p-2 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400" data-id="element-3032" data-mp="2ak933zwv3og1">
                  <WifiOff className="w-5 h-5" data-id="element-3033" data-mp="25mpec62jft9u" />
                </div>
                <h3 className={`font-bold ${isLight ? 'text-amber-900' : 'text-amber-400'}`} data-id="element-3034" data-mp="339og2v837rel">
                  Temporarily Turn Off Wi-Fi
                </h3>
              </div>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-amber-800' : 'text-amber-200/80'}`} data-id="element-3035" data-mp="2taz4fk6sb36s">
                Some mobile carriers require Wi-Fi to be turned off while
                activating call forwarding using carrier activation codes.
              </p>
              <p className={`text-sm leading-relaxed mt-3 font-medium ${isLight ? 'text-amber-900' : 'text-amber-400'}`} data-id="element-3036" data-mp="1dui87zknrz9p">
                You can turn Wi-Fi back on immediately after setup.
              </p>
            </div>

            <div className="mt-auto space-y-3" data-id="element-3037" data-mp="1k8xgl5axarlh">
              <Button fullWidth onClick={() => setStep(6)} data-id="element-3038" data-mp="15eizxbetzu9z">
                Wi-Fi is Off
              </Button>
              <button onClick={() => setShowWifiSheet(true)} className={`w-full py-3 text-sm font-medium ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-3039" data-mp="1abj3nfo7kkxx">
                Learn More
              </button>
            </div>
          </motion.div>;
      case 6:
        return <motion.div key="step6" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-3040" data-mp="rltasy40a98n">
            <div className="mb-8" data-id="element-3041" data-mp="3q65u7n6zzsmz">
              <h1 className={`text-2xl font-bold mb-4 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3042" data-mp="3q77e8h5zrpgu">
                Connecting Your Phone
              </h1>
              <div className="flex items-center gap-2" data-id="element-3043" data-mp="316mo7nzfx94">
                {conditionsToActivate.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= currentConditionIdx ? 'bg-vexu' : isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-3044" data-mp="hxfmpbfp6eml" data-mpi={i} />)}
              </div>
              <p className={`text-xs mt-2 font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3045" data-mp="mpfg0l19i9a7">
                Step {currentConditionIdx + 1} of {conditionsToActivate.length}
              </p>
            </div>

            <AnimatePresence mode="wait" data-id="element-3046" data-mp="vrga59178f7n">
              {verificationState === 'idle' && <motion.div key="idle" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6" data-id="element-3047" data-mp="1frgkqlhiaoym">
                  <div className={`p-5 rounded-3xl ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-white/5 border border-white/10'}`} data-id="element-3048" data-mp="wvqgssq0jii3">
                    <h2 className={`text-lg font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3049" data-mp="15w54fyqg99or">
                      {currentCondition?.title}
                    </h2>
                    <p className={`text-sm mb-6 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3050" data-mp="2m8bs9evwxmwm">
                      We'll activate your carrier's call forwarding so your AI
                      assistant can answer.
                    </p>

                    <div className="mb-3" data-id="element-3051" data-mp="5o87g39q29iz">
                      <label className={`text-xs font-bold uppercase tracking-wider mb-1.5 block ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3052" data-mp="u3wl8xc5hcm">
                        Carrier Activation Code (USSD)
                      </label>
                      <div className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-black/20 border-white/10'}`} data-id="element-3053" data-mp="3j08ry5cw2mfc">
                        <code className={`text-base font-mono font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3054" data-mp="393nnbl4s7dc1">
                          {currentCondition?.code}
                        </code>
                        <button onClick={() => handleCopy(currentCondition?.code || '')} className={`p-2 rounded-lg transition-colors ${copied ? 'bg-vexu/10 text-vexu' : isLight ? 'bg-white border border-slate-200 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3055" data-mp="31rg0h8ohuxwh">
                          {copied ? <CheckCircle2 className="w-4 h-4" data-id="element-3056" data-mp="3khnv12e4og12" /> : <Copy className="w-4 h-4" data-id="element-3057" data-mp="3phacjo0zoifo" />}
                        </button>
                      </div>
                    </div>
                    <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3058" data-mp="3fiuhxb2r6r1f">
                      This code tells your mobile carrier to forward calls when
                      your phone is busy, allowing your AI assistant to answer
                      on your behalf.
                    </p>
                  </div>

                  <div data-id="element-3059" data-mp="1rl3kxymt1wst">
                    <Button fullWidth onClick={() => {
                  // Launch the Phone app with the carrier activation code
                  // prefilled, then show the verification prompt.
                  const code = currentCondition?.code || '';
                  if (code) {
                    window.open('tel:' + encodeURIComponent(code), '_self');
                  }
                  setVerificationState('verifying');
                }} className="flex items-center justify-center gap-2" data-id="element-3060" data-mp="1rllg0rxqe4z5">
                      <Phone className="w-4 h-4" data-id="element-3061" data-mp="v1w51mapjq8r" />
                      Dial Activation Code
                    </Button>
                    <p className={`text-xs leading-relaxed mt-3 text-center ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3062" data-mp="q2027qdqy4us">
                      Your Phone app will open with the activation code ready to
                      dial. After completing the activation, return to Vexu to
                      continue the setup.
                    </p>
                  </div>
                </motion.div>}

              {verificationState === 'verifying' && <motion.div key="verifying" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6" data-id="element-3063" data-mp="siqgroyukt2v">
                  <div className="text-center" data-id="element-3064" data-mp="1ba9htq9b9x29">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-3065" data-mp="1b6vcqa99j9r4">
                      <CheckCircle2 className="w-8 h-8" data-id="element-3066" data-mp="2mqaq2rvmlrq1" />
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3067" data-mp="2msf2jy7lbz1x">
                      Confirm Carrier Activation
                    </h2>
                    <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3068" data-mp="3afmz9rqydzsc">
                      Check the message displayed by your carrier after dialing
                      the activation code. Continue only if you see a successful
                      activation confirmation.
                    </p>
                  </div>

                  {/* Example success message mockups */}
                  <div className="space-y-2.5" data-id="element-3069" data-mp="3td6an20vvuhx">
                    {['Setting Interrogation Succeeded', 'Call Forwarding\nForwarded to ' + VEXU_NUMBER].map((msg, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.1 + i * 0.1
                }} className={`rounded-2xl px-4 py-3 text-center ${isLight ? 'bg-slate-100 border border-slate-200' : 'bg-white/5 border border-white/10'}`} data-id="element-3070" data-mp="3v78uqr8eelwe" data-mpi={i}>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3071" data-mp="rqrz4rldo7fa">
                          Example carrier message
                        </p>
                        <p className={`text-sm font-medium whitespace-pre-line ${isLight ? 'text-slate-800' : 'text-zinc-200'}`} data-id="element-3072" data-mp="1adj1v710jm32">
                          {msg}
                        </p>
                      </motion.div>)}
                  </div>

                  {/* What success looks like */}
                  <div className={`p-5 rounded-3xl ${isLight ? 'bg-vexu/5 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-3073" data-mp="rfg86exit0yx">
                    <h3 className={`text-sm font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3074" data-mp="dmgrwapwz2qb">
                      You're ready to continue if you see:
                    </h3>
                    <ul className="space-y-2.5" data-id="element-3075" data-mp="8pqclplxob5y">
                      {['Activation Successful', 'Registration Successful', 'Call Forwarding Activated', 'Service Enabled', 'Your Vexu forwarding number shown as Active, Registered, or Enabled'].map(item => <li key={item} className="flex items-start gap-2.5 text-sm" data-id="element-3076" data-mp="371v2m45sh711">
                          <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-3077" data-mp="3kd5orrk1kfe" />
                          <span className={`leading-relaxed ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-3078" data-mp="3usr0nj0u8ygq">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="space-y-3 pt-1" data-id="element-3079" data-mp="tun8yumbqoh5">
                    <Button fullWidth onClick={handleConditionSuccess} data-id="element-3080" data-mp="tw6sqv76vhno">
                      ✓ I Saw a Success Message
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setVerificationState('troubleshoot')} data-id="element-3081" data-mp="13s1gg4q9s2xb">
                      ↻ Try Again
                    </Button>
                    <button onClick={() => setVerificationState('troubleshoot')} className={`w-full text-sm font-medium pt-1 ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-3082" data-mp="1dkfikyl9fbk0">
                      Having Trouble?
                    </button>
                  </div>
                </motion.div>}

              {verificationState === 'troubleshoot' && <motion.div key="troubleshoot" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6 pt-4" data-id="element-3083" data-mp="3ta0rugmlfn19">
                  <div className="text-center mb-6" data-id="element-3084" data-mp="14ugzwvjmllu6">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/10 text-red-400'}`} data-id="element-3085" data-mp="14v8ohmet2jay">
                      <AlertTriangle className="w-8 h-8" data-id="element-3086" data-mp="1dovf6y45owda" />
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3087" data-mp="18v0y82fxqvhw">
                      Activation Didn't Work
                    </h2>
                    <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3088" data-mp="h3bjae5v2pit">
                      We couldn't confirm the activation. This may happen for
                      one of the following reasons:
                    </p>
                  </div>

                  <ul className={`text-sm space-y-3 p-5 rounded-2xl ${isLight ? 'bg-slate-50 text-slate-700' : 'bg-white/5 text-zinc-300'}`} data-id="element-3089" data-mp="21uzpgj08hr2">
                    <li className="flex items-start gap-2" data-id="element-3090" data-mp="ppuselzvmi8a">
                      <span className="text-vexu mt-0.5" data-id="element-3091" data-mp="uph0yi4rfaar">•</span> Wi-Fi is still
                      enabled.
                    </li>
                    <li className="flex items-start gap-2" data-id="element-3092" data-mp="fvvphkrzaufa">
                      <span className="text-vexu mt-0.5" data-id="element-3093" data-mp="krb1062ike8j">•</span> The activation
                      was cancelled before completion.
                    </li>
                    <li className="flex items-start gap-2" data-id="element-3094" data-mp="35ileajsnk34d">
                      <span className="text-vexu mt-0.5" data-id="element-3095" data-mp="35k4pvx3d5pzt">•</span> Your carrier
                      requires a different activation code.
                    </li>
                    <li className="flex items-start gap-2" data-id="element-3096" data-mp="1722o1qgepvds">
                      <span className="text-vexu mt-0.5" data-id="element-3097" data-mp="12ab6u6j5520b">•</span> Your mobile
                      plan doesn't support call forwarding.
                    </li>
                  </ul>

                  <div className="space-y-3 pt-4" data-id="element-3098" data-mp="x93xi31cnurj">
                    <Button fullWidth onClick={() => setVerificationState('idle')} data-id="element-3099" data-mp="sd6iq15qiwgv">
                      Try Again
                    </Button>
                    <Button fullWidth variant="secondary" onClick={onContactSupport} data-id="element-3100" data-mp="jc8crdbqkevn">
                      Contact Support
                    </Button>
                    {isAddRule ? <button onClick={onBack} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-3101" data-mp="28q3ql0viudmm">
                        Cancel
                      </button> : <button onClick={() => setVerificationState('cancelConfirm')} className="w-full py-3 text-sm font-medium text-red-500 hover:text-red-600" data-id="element-3102" data-mp="2q21ykoehbvpl">
                        Cancel Subscription
                      </button>}
                  </div>
                </motion.div>}

              {verificationState === 'cancelConfirm' && <motion.div key="cancelConfirm" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6 pt-8 text-center" data-id="element-3103" data-mp="2c9ce4oelmyul">
                  <h2 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3104" data-mp="mbnt06uscv8v">
                    Cancel Your Subscription?
                  </h2>
                  <p className={`text-sm mb-6 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3105" data-mp="vc2n9vpq9koi">
                    Your AI assistant requires carrier call forwarding to work.
                    If your carrier or mobile plan isn't compatible, you can
                    cancel your subscription now.
                  </p>
                  <div className="space-y-3" data-id="element-3106" data-mp="109vwkxmbuut0">
                    <Button fullWidth onClick={() => setVerificationState('troubleshoot')} data-id="element-3107" data-mp="3737rluv0y342">
                      Keep Subscription
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setStep(8)} // Cancel success screen
                data-id="element-3108" data-mp="1hlt0tg296ne5">
                      <span className="text-red-500" data-id="element-3109" data-mp="17uy1b9zzxrar">Cancel Subscription</span>
                    </Button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>;
      case 7:
        return <motion.div key="step7" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-3110" data-mp="13tw9vfcbmjjc">
            <div className="mt-12 mb-8 text-center" data-id="element-3111" data-mp="2o97tkf7d006v">
              <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/20'}`} data-id="element-3112" data-mp="2o8ohvg8ay81a">
                <span className="text-4xl" data-id="element-3113" data-mp="sinrrs1g8ve3">🎉</span>
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3114" data-mp="ekpe6ouyd2zn">
                {isAddRule ? 'Rule Added' : '🎉 Your AI Is Live'}
              </h1>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3115" data-mp="4tbk4wlrsvdw">
                {isAddRule ? 'This answering rule is now active. Your AI will answer calls in this situation too.' : 'Your AI assistant is connected and ready to answer your calls.'}
              </p>
            </div>

            {/* Enabled forwarding conditions */}
            <div className={`p-5 rounded-3xl mb-6 ${isLight ? 'bg-vexu/5 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-3116" data-mp="3i2zi0888i9qv">
              <h3 className={`text-sm font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3117" data-mp="33f8z9emtc3ro">
                Your AI now answers
              </h3>
              <ul className="space-y-2.5" data-id="element-3118" data-mp="3dcnb10xsk4dn">
                {conditionsToActivate.map((c, i) => <motion.li key={c.title} initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.15 + i * 0.08
              }} className="flex items-center gap-2.5 text-sm" data-id="element-3119" data-mp="38d0wmo688i39" data-mpi={i}>
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-3120" data-mp="9v5734l53hii" />
                    <span className={`font-medium ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-3121" data-mp="4uh5jozabia1">
                      {c.title}
                    </span>
                  </motion.li>)}
              </ul>
            </div>

            {!isAddRule && <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className={`p-5 rounded-3xl mb-8 ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-white/5 border border-white/10'}`} data-id="element-3122" data-mp="qoo65agzt6rq">
                <div className="flex items-start gap-3 mb-3" data-id="element-3123" data-mp="19mr105w81xxg">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-3124" data-mp="36glvt18frkjx">
                    <Users className="w-5 h-5" data-id="element-3125" data-mp="33wfn6g8vn5x1" />
                  </div>
                  <div data-id="element-3126" data-mp="2z44eop02mbsv">
                    <h3 className={`text-base font-bold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3127" data-mp="2z3mjlvp5a3mj">
                      Personalize Your AI
                    </h3>
                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-3128" data-mp="2p8b4hc9tque9">
                      Recommended
                    </span>
                  </div>
                </div>
                <p className={`text-sm leading-relaxed mb-3 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3129" data-mp="3cuhlup551mh5">
                  Make your AI respond differently for family, friends, work
                  contacts, clients, and unknown callers.
                </p>
                <p className={`text-sm leading-relaxed mb-4 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3130" data-mp="1wg9bru1k15me">
                  You can customize greetings, tone of voice, languages,
                  calendar access, AI instructions, and call behaviour for each
                  caller group.
                </p>
                <div className={`rounded-2xl px-4 py-3 ${isLight ? 'bg-slate-50 border border-slate-100' : 'bg-white/[0.03] border border-white/5'}`} data-id="element-3131" data-mp="3gcnhetig0j1a">
                  <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3132" data-mp="cw6myx75salr">
                    Every caller currently uses your{' '}
                    <span className={`font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-3133" data-mp="2y0pqswarsn8">
                      Default AI profile
                    </span>
                    . You can edit it or create your first custom caller group
                    at any time.
                  </p>
                </div>
              </motion.div>}

            <div className="mt-auto space-y-3" data-id="element-3134" data-mp="10rs3mvzqsq1h">
              {isAddRule ? <Button fullWidth onClick={() => onComplete(selectedConditions)} data-id="element-3135" data-mp="10twb1nwuo76i">
                  Done
                </Button> : <>
                  <Button fullWidth onClick={() => onComplete(selectedConditions, 'callerExperience')} data-id="element-3136" data-mp="mzls5n7jb78d">
                    Customize My AI
                  </Button>
                  <Button fullWidth variant="secondary" onClick={() => onComplete(selectedConditions, 'home')} data-id="element-3137" data-mp="vu9wnb5ackw4">
                    Maybe Later
                  </Button>
                </>}
            </div>
          </motion.div>;
      case 8:
        // Cancel Success
        return <motion.div key="step8" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col items-center justify-center p-6 text-center" data-id="element-3138" data-mp="1yd7yt9w13dpk">
            <div className={`w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3139" data-mp="1fijalywhxhx3">
              <CheckCircle2 className="w-8 h-8" data-id="element-3140" data-mp="1pgp69yrplrti" />
            </div>
            <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3141" data-mp="1uegzcw43bjza">
              Subscription Cancelled
            </h1>
            <p className={`text-sm leading-relaxed mb-8 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3142" data-mp="35s2wp4k3srvw">
              Your subscription has been cancelled successfully. You're always
              welcome to return and activate Vexu whenever your carrier supports
              call forwarding.
            </p>
            <Button fullWidth onClick={onCancel} data-id="element-3143" data-mp="kdmox8x89l1r">
              Return to Home
            </Button>
          </motion.div>;
    }
  };
  return <div className={`h-full w-full relative overflow-hidden ${isLight ? 'bg-stone-100' : 'bg-black'}`} data-id="element-3144" data-mp="ai1k730ry6ob">
      <AnimatePresence mode="wait" data-id="element-3145" data-mp="6evcrs1xe9g9">{renderStep()}</AnimatePresence>
      <ConsentInfoSheet open={showConsentSheet} onClose={() => setShowConsentSheet(false)} data-id="element-3146" data-mp="1glhlhg7b8un" />
      <WifiInfoSheet open={showWifiSheet} onClose={() => setShowWifiSheet(false)} data-id="element-3147" data-mp="3snocojz5tza5" />
    </div>;
}